import { useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../styling/stylingHelper';
import {
  getFlaggedProductOrders,
  recheckFlaggedProduct,
  type FlaggedProductWithRestaurant,
  type OrderLookupResult,
  type RecheckFlaggedProductResult,
} from '../../../../../services/admin/adminService';
import { ChevronDown, ChevronRight, Eye, FileText, RotateCw } from 'lucide-react';
import { GmailIdCopy } from '../../../orderHistory/GmailCopy';
import { OrderLookupFactuurBlock, OrderLookupProductItem } from '../shared/OrderLookupBlocks';
import type { TFn } from '../types';

// Eye-icon detail modal for a single flagged product: shows which restaurant ordered it, the current
// quantity info, the last few orders it appeared in (open the invoice file directly), and a "Recheck"
// button that re-runs the AI quantity audit on its current data so the operator can confirm it's
// resolved before marking the flag done.
export function FlaggedProductDetailButton({ product, t }: { product: FlaggedProductWithRestaurant; t: TFn }) {
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState<OrderLookupResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [recheck, setRecheck] = useState<RecheckFlaggedProductResult | null>(null);
  const [rechecking, setRechecking] = useState(false);

  const ref = {
    setupSource: product.setupSource,
    platform: product.platform,
    productName: product.productName,
    articleId: product.articleId,
    restaurantName: product.restaurantName,
    gmailId: product.gmailId,
    source: product.source ?? 'pakbon' as const,
  };

  const load = async () => {
    setOpen(true);
    if (orders !== null) return;
    setLoading(true);
    setError(null);
    try {
      setOrders(await getFlaggedProductOrders(ref));
    } catch (e: any) {
      setError(e.message || t('adminTesting.flaggedDetail.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const runRecheck = async () => {
    if (rechecking) return;
    setRechecking(true);
    setError(null);
    try {
      const res = await recheckFlaggedProduct({ ...ref, flaggedQuantityInfo: product.quantityInfo });
      setRecheck(res);
      setOrders(res.orders); // recheck returns the same recent orders — keep the list fresh
    } catch (e: any) {
      setError(e.message || t('adminTesting.flaggedDetail.recheckError'));
    } finally {
      setRechecking(false);
    }
  };

  const toggle = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <>
      <button
        onClick={load}
        title={t('adminTesting.flaggedDetail.buttonTitle')}
        className={cn('p-1 rounded-md text-gray-500 dark:text-gray-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-colors')}
      >
        <Eye className="w-4 h-4" />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4" onClick={() => setOpen(false)}>
          <div className={cn(cardClass, 'w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto')} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className={cn(primaryText, semiBoldText, 'truncate')} title={product.productName}>{product.productName}</h3>
                <p className={cn(secondaryText, 'text-xs mt-0.5 font-mono')}>
                  {product.platform}{product.articleId ? ` · ${product.articleId}` : ''} · {product.restaurantName}
                </p>
              </div>
              <button onClick={() => setOpen(false)} className={cn(secondaryText, 'hover:opacity-70')} aria-label="Close">✕</button>
            </div>

            {/* Flag reason + current quantity info */}
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('text-xs px-2 py-1 rounded bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300')}>
                {product.reason}
              </span>
              <span className={cn(secondaryText, 'text-xs font-mono')}>
                {t('adminTesting.flaggedDetail.current')}:{' '}
                {product.quantityInfo
                  ? `${product.quantityInfo.stdMultiplier} × ${product.quantityInfo.stdAmount}${product.quantityInfo.stdUnit}`
                  : 'null'}
              </span>
            </div>

            {/* Recheck */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={runRecheck}
                disabled={rechecking}
                className={cn('px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 text-white dark:text-white text-xs', semiBoldText, 'disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5')}
              >
                <RotateCw className={cn('w-3.5 h-3.5', rechecking && 'animate-spin')} />
                {rechecking ? t('adminTesting.flaggedDetail.rechecking') : t('adminTesting.flaggedDetail.recheck')}
              </button>
              {recheck && (
                recheck.inconclusive ? (
                  <span className={cn('text-xs px-2 py-1 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300')}>
                    {t('adminTesting.review.recheckInconclusive')}
                  </span>
                ) : recheck.stillFlagged ? (
                  <span className={cn('text-xs px-2 py-1 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300')}>
                    {t('adminTesting.flaggedDetail.stillFlagged', { reason: recheck.flaggedProducts[0]?.reason ?? product.reason })}
                  </span>
                ) : (
                  <span className={cn('text-xs px-2 py-1 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300')}>
                    {t('adminTesting.flaggedDetail.resolved')}
                  </span>
                )
              )}
            </div>

            {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}

            {/* Recent orders */}
            <div>
              <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-2')}>{t('adminTesting.flaggedDetail.recentOrders')}</div>
              {loading ? (
                <div className={cn(secondaryText, 'text-xs')}>{t('adminTesting.flaggedDetail.loading')}</div>
              ) : !orders || orders.length === 0 ? (
                <div className={cn(secondaryText, 'text-xs')}>{t('adminTesting.flaggedDetail.noOrders')}</div>
              ) : (
                <div className="space-y-2">
                  {orders.map((o) => {
                    const isOpen = expanded.has(o.id);
                    return (
                      <div key={o.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                        <div className="flex items-center gap-2 px-3 py-2">
                          <button onClick={() => toggle(o.id)} className="flex items-center gap-1.5 flex-1 min-w-0 text-left">
                            {isOpen ? <ChevronDown className="w-4 h-4 shrink-0 text-gray-400 dark:text-textQuaternary-dark" /> : <ChevronRight className="w-4 h-4 shrink-0 text-gray-400 dark:text-textQuaternary-dark" />}
                            <span className={cn(primaryText, smallTextSize, semiBoldText, 'truncate')}>{o.orderNumber}</span>
                            <span className={cn(secondaryText, 'text-xs whitespace-nowrap')}>{new Date(o.orderDate).toLocaleDateString('nl-NL')}</span>
                            <span className={cn(secondaryText, 'text-xs truncate')}>{o.restaurantName}</span>
                          </button>
                          <GmailIdCopy gmailId={o.gmailId ?? undefined} />
                          {o.sourceFileUrl ? (
                            <a href={o.sourceFileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 dark:text-textBlue-dark hover:underline whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                              <FileText className="w-4 h-4" />
                              {t('adminTesting.flaggedDetail.seeFile')}
                            </a>
                          ) : (
                            <span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.flaggedDetail.noFile')}</span>
                          )}
                        </div>
                        {isOpen && (
                          <div className="px-3 pb-3 space-y-2">
                            {o.facturen.map(f => (
                              <OrderLookupFactuurBlock
                                key={f.factuurOrderId}
                                factuur={f}
                                platform={o.platform}
                                orderTotal={o.total}
                                t={t}
                              />
                            ))}
                            {[...o.products]
                              .sort((a, b) => {
                                // Surface the flagged product first.
                                const am = (a.name ?? '').toUpperCase() === product.productName.toUpperCase() ? 0 : 1;
                                const bm = (b.name ?? '').toUpperCase() === product.productName.toUpperCase() ? 0 : 1;
                                return am - bm;
                              })
                              .map((p, i) => (
                                <OrderLookupProductItem key={p.articleId || i} product={p} platform={o.platform} />
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end">
              <button onClick={() => setOpen(false)} className={cn('px-3 py-2 rounded-lg', secondaryText, smallTextSize, 'hover:bg-gray-100 dark:hover:bg-gray-700')}>{t('adminTesting.flaggedDetail.close')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
